// Web Audio recipes for the 12 sounds. The same code plays live (fallback) and renders the recorded loops in
// apps/web/public/audio/sounds (tools/sounds/render.py, OfflineAudioContext), so the file and the live sound match.
// Recipes follow the CI reference (design/ci-v2/ci/app-home.html).
import type { SoundId } from './catalog';
import { LEVEL } from './levels';

export type Voice = { out: GainNode; stop: (fadeSec?: number) => void };

export type SynthOpts = {
  /** Offline render: schedule every rhythmic event up to this time (s) at once instead of on a live timer. */
  until?: number;
  /** Length of the looped noise buffers (s). Live: 3 s; a render uses the loop length so the noise repeats only once per file. */
  noiseSec?: number;
  /** Output level; default LEVEL[id] (measured by the render tool so all sounds are equally loud). */
  level?: number;
  /** Random source (a seeded one makes renders reproducible). */
  random?: () => number;
};

const bufCache = new WeakMap<BaseAudioContext, Record<string, AudioBuffer>>();
/** Seamless wrap of a looped noise buffer: the last samples blend into the first (equal-power), no click every loop. */
const WRAP_SEC = 0.1;

function noise(ac: BaseAudioContext, kind: 'white' | 'pink' | 'brown', sec: number, random: () => number): AudioBuffer {
  const cache = bufCache.get(ac) ?? {};
  bufCache.set(ac, cache);
  const key = `${kind}:${sec}`;
  if (cache[key]) return cache[key]!;
  const len = Math.round(ac.sampleRate * sec);
  const fade = Math.round(ac.sampleRate * WRAP_SEC);
  const d = new Float32Array(len + fade);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, last = 0;
  for (let i = 0; i < d.length; i++) {
    const w = random() * 2 - 1;
    if (kind === 'white') d[i] = w * 0.5;
    else if (kind === 'brown') { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.5; }
    else {
      b0 = 0.99886 * b0 + w * 0.0555179; b1 = 0.99332 * b1 + w * 0.0750759; b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856; b4 = 0.55 * b4 + w * 0.5329522; b5 = -0.7616 * b5 - w * 0.016898;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11; b6 = w * 0.115926;
    }
  }
  // the buffer's end runs on into d[len…]; the start fades in from that continuation
  for (let i = 0; i < fade; i++) {
    const t = (i / fade) * (Math.PI / 2);
    d[i] = d[i]! * Math.sin(t) + d[len + i]! * Math.cos(t);
  }
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  buf.getChannelData(0).set(d.subarray(0, len));
  cache[key] = buf;
  return buf;
}

const LULLABY = 'E4 .5,E4 .5,G4 2,E4 .5,E4 .5,G4 2,E4 .5,G4 .5,C5 1,B4 1,A4 1,A4 1,G4 1,D4 .5,E4 .5,F4 1,D4 1,D4 .5,E4 .5,F4 2,D4 .5,F4 .5,B4 .5,A4 .5,G4 1,B4 1,C5 2,C4 .5,C4 .5,C5 2,A4 .5,F4 .5,G4 2,E4 .5,C4 .5,F4 1,G4 1,A4 1,G4 2,C4 .5,C4 .5,C5 2,A4 .5,F4 .5,G4 2,E4 .5,C4 .5,F4 1,E4 1,D4 1,C4 3'
  .split(',')
  .map((x) => {
    const [n, b] = x.split(' ') as [string, string];
    const st = ({ C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 } as Record<string, number>)[n[0]!]! + (Number(n[1]) - 4) * 12;
    return [440 * 2 ** (st / 12), Number(b)] as const;
  });

export function synthVoice(ac: BaseAudioContext, dest: AudioNode, id: SoundId, opts: SynthOpts = {}): Voice {
  const random = opts.random ?? Math.random;
  const noiseSec = opts.noiseSec ?? 3;
  const offline = opts.until != null;
  /** Schedule rhythmic events up to this time: a short lookahead live, the whole render offline. */
  const ahead = (sec: number) => opts.until ?? ac.currentTime + sec;
  const nodes: AudioScheduledSourceNode[] = [];
  const timers: number[] = [];
  const out = ac.createGain();
  out.gain.value = 0;
  out.connect(dest);

  const gain = (v: number) => { const n = ac.createGain(); n.gain.value = v; return n; };
  const filt = (type: BiquadFilterType, f: number, q?: number, g?: number) => {
    const n = ac.createBiquadFilter(); n.type = type; n.frequency.value = f;
    if (q != null) n.Q.value = q; if (g != null) n.gain.value = g; return n;
  };
  const chain = (...ns: AudioNode[]) => { ns.reduce((a, b) => a.connect(b)); return ns[ns.length - 1]!; };
  const src = (kind: 'white' | 'pink' | 'brown') => {
    const n = ac.createBufferSource(); n.buffer = noise(ac, kind, noiseSec, random); n.loop = true; n.start(0, random() * noiseSec * 0.8); nodes.push(n); return n;
  };
  const osc = (type: OscillatorType, f: number) => { const n = ac.createOscillator(); n.type = type; n.frequency.value = f; n.start(); nodes.push(n); return n; };
  const lfo = (f: number, depth: number, param: AudioParam) => { chain(osc('sine', f), gain(depth)).connect(param); };
  const every = (ms: number, fn: () => void) => {
    fn();
    // offline: no clock runs while scheduling, so call the step as often as the render needs
    if (offline) for (let k = Math.ceil((opts.until! * 1000) / ms); k > 0; k--) fn();
    else timers.push(window.setInterval(fn, ms));
  };
  const thumps = (bpm: number, amp: number) => {
    let next = ac.currentTime + 0.1;
    every(200, () => {
      while (next < ahead(0.6)) {
        for (const [dt, a] of [[0, amp], [0.3, amp * 0.65]] as const) {
          const t = next + dt, o = ac.createOscillator(), g = gain(0);
          o.frequency.setValueAtTime(95, t); o.frequency.exponentialRampToValueAtTime(48, t + 0.16);
          g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(a, t + 0.012); g.gain.exponentialRampToValueAtTime(0.0008, t + 0.24);
          o.connect(g).connect(out); o.start(t); o.stop(t + 0.3);
        }
        next += 60 / bpm;
      }
    });
  };
  const musicbox = () => {
    const beat = 0.48; let i = 0, next = ac.currentTime + 0.1;
    every(150, () => {
      while (next < ahead(0.5)) {
        const [f, b] = LULLABY[i]!;
        for (const [m, a] of [[1, 0.22], [2, 0.07], [3.01, 0.035], [5.4, 0.012]] as const) {
          const o = ac.createOscillator(), g = gain(0); o.type = 'sine'; o.frequency.value = f * m;
          g.gain.setValueAtTime(0, next); g.gain.linearRampToValueAtTime(a, next + 0.004); g.gain.exponentialRampToValueAtTime(0.0005, next + 1.6 / m ** 0.3);
          o.connect(g).connect(out); o.start(next); o.stop(next + 1.8);
        }
        next += b * beat; i++;
        if (i === LULLABY.length) { i = 0; next += 1.5; }
      }
    });
  };

  switch (id) {
    case 'shush': {
      const amp = gain(0);
      chain(src('pink'), filt('highpass', 1400), filt('peaking', 3200, 0.9, 8), filt('lowpass', 7500), amp, out);
      let next = ac.currentTime + 0.05;
      every(1900 * 4, () => {
        if (next >= ahead(7.6)) return;
        for (let j = 0; j < 4; j++) { const t = next + j * 1.9; amp.gain.setTargetAtTime(0.42, t, 0.12); amp.gain.setTargetAtTime(0.14, t + 1.33, 0.18); }
        next += 7.6;
      });
      break;
    }
    case 'white': chain(src('white'), filt('lowpass', 8000), gain(0.3), out); break;
    case 'womb': chain(src('brown'), filt('lowpass', 380), gain(0.7), out); thumps(62, 0.35); break;
    case 'heart': chain(src('brown'), filt('lowpass', 200), gain(0.12), out); thumps(70, 0.9); break;
    case 'hair': chain(src('pink'), filt('highpass', 400), filt('peaking', 2000, 0.8, 6), filt('lowpass', 6000), gain(0.4), out); chain(osc('sawtooth', 175), filt('lowpass', 500), gain(0.03), out); break;
    case 'fan': { const amp = gain(0.6); chain(src('brown'), filt('lowpass', 900), amp, out); lfo(11, 0.06, amp.gain); lfo(0.07, 0.1, amp.gain); break; }
    case 'vacuum': chain(src('pink'), filt('highpass', 250), filt('bandpass', 1100, 0.6), gain(1.1), out); chain(osc('sine', 1250), gain(0.008), out); break;
    case 'washer': { chain(src('brown'), filt('lowpass', 500), gain(0.45), out); const amp = gain(0.12); chain(src('pink'), filt('bandpass', 800, 0.8), amp, out); lfo(0.42, 0.11, amp.gain); break; }
    case 'car': { const amp = gain(1); chain(src('brown'), filt('lowpass', 220), amp, out); lfo(0.15, 0.1, amp.gain); chain(osc('sine', 48), gain(0.05), out); break; }
    case 'rain': chain(src('white'), filt('highpass', 900), filt('lowpass', 7000), gain(0.22), out); chain(src('pink'), filt('bandpass', 3500, 1.2), gain(0.08), out); break;
    case 'ocean': { const amp = gain(0.32); chain(src('brown'), filt('lowpass', 1000), amp, out); lfo(0.11, 0.28, amp.gain); const foam = gain(0.04); chain(src('white'), filt('highpass', 2000), filt('lowpass', 5000), foam, out); lfo(0.11, 0.035, foam.gain); break; }
    case 'music': musicbox(); break;
  }
  out.gain.setTargetAtTime(opts.level ?? LEVEL[id] ?? 1, ac.currentTime, 0.15);

  return {
    out,
    stop(fade = 0.5) {
      out.gain.cancelScheduledValues(ac.currentTime);
      out.gain.setTargetAtTime(0, ac.currentTime, fade / 4);
      window.setTimeout(() => {
        timers.forEach(clearInterval);
        nodes.forEach((n) => { try { n.stop(); } catch { /* already stopped */ } });
        out.disconnect();
      }, fade * 1000 + 200);
    },
  };
}
